// user directory — resolves an email to a person's display name for rendering across the app.
// the audit log deliberately keeps raw emails; everywhere else shows the display name.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export interface DirectoryEntry {
  email: string;
  displayName: string;
  /** an address merged into this account (Users › Merge): it names that person in history, but pickers
   *  leave it out, the person is listed once under the account they sign in as. */
  aliasOf?: string;
}

export function useDirectory() {
  return useQuery({
    queryKey: ['directory'],
    queryFn: () => api.get<{ users: DirectoryEntry[] }>('/api/directory'),
    staleTime: 5 * 60_000, // names rarely change; cache for 5 min
  });
}

/**
 * returns a resolver `(email) => name`. falls back to the raw email when the person
 * isn't in the directory (e.g. a system actor), and to '—' for empty values.
 */
export function useNameResolver(): (email?: string | null) => string {
  const { data } = useDirectory();
  const map = useMemo(() => {
    const m = new Map<string, string>();
    for (const u of data?.users ?? []) m.set(u.email.toLowerCase(), u.displayName);
    return m;
  }, [data]);
  return useMemo(
    () => (email?: string | null) => {
      const e = (email ?? '').trim();
      if (!e) return '-';
      return map.get(e.toLowerCase()) ?? e;
    },
    [map],
  );
}
